"use client";

import { createContext, type ReactNode, use, useEffect, useState } from "react";

import type {
  EthereumVirtualMachineChainId,
  SolanaVirtualMachineClusterName,
  VirtualMachine,
} from "@/config/vm-feature-config";
import {
  DEFAULT_ETHEREUM_VIRTUAL_MACHINE_CHAIN_ID,
  DEFAULT_SOLANA_VIRTUAL_MACHINE_CLUSTER_NAME,
  DEFAULT_VIRTUAL_MACHINE,
} from "@/config/vm-feature-config";

import { localStorageHelpers } from "@/lib/utils/storage-apis";

type VirtualMachineState = ReturnType<typeof useVirtualMachine>;

const VirtualMachineContext = createContext<VirtualMachineState>(null!);

const VIRTUAL_MACHINE_KEY = "__agora_last_used_virtual_machine";
const ETHEREUM_CHAINID_KEY = "__agora_last_used_ethereum_chain_id";
const SOLANA_CLUSTERNAME_KEY = "__agora_last_used_solana_cluster_name";

// import this across the app
export function useVirtualMachineContext(): VirtualMachineState {
  return use(VirtualMachineContext);
}

export function VirtualMachineProvider({ children }: { children: ReactNode }) {
  // Get initial values from cookies with defaults
  const getInitialVM = (): VirtualMachine => {
    const saved = localStorageHelpers.get(VIRTUAL_MACHINE_KEY) as VirtualMachine;
    return saved || DEFAULT_VIRTUAL_MACHINE;
  };

  const getInitialEthereumChainId = (): EthereumVirtualMachineChainId => {
    const saved = localStorageHelpers.get(ETHEREUM_CHAINID_KEY);
    return saved ? parseInt(`${saved}`, 10) : DEFAULT_ETHEREUM_VIRTUAL_MACHINE_CHAIN_ID;
  };

  const getInitialSolanaCluster = (): SolanaVirtualMachineClusterName => {
    const saved = localStorageHelpers.get(SOLANA_CLUSTERNAME_KEY) as SolanaVirtualMachineClusterName;
    return saved || DEFAULT_SOLANA_VIRTUAL_MACHINE_CLUSTER_NAME;
  };

  const globalExchangeState = useVirtualMachine({
    lastUsedEthereumChainId: getInitialEthereumChainId(),
    lastUsedSolanaClusterName: getInitialSolanaCluster(),
    lastUsedVirtualMachine: getInitialVM(),
  });

  return <VirtualMachineContext value={globalExchangeState}>{children}</VirtualMachineContext>;
}

function useVirtualMachine({
  lastUsedEthereumChainId,
  lastUsedSolanaClusterName,
  lastUsedVirtualMachine,
}: {
  lastUsedEthereumChainId: EthereumVirtualMachineChainId;
  lastUsedSolanaClusterName: SolanaVirtualMachineClusterName;
  lastUsedVirtualMachine: VirtualMachine;
}) {
  const [activeVirtualMachine, setActiveVirtualMachine] = useState<VirtualMachine>(lastUsedVirtualMachine);

  const [ethereumVirtualMachineChainId, setEthereumVirtualMachineChainId] =
    useState<EthereumVirtualMachineChainId>(lastUsedEthereumChainId);

  const [solanaVirtualMachineClusterName, setSolanaVirtualMachineClusterName] =
    useState<SolanaVirtualMachineClusterName>(lastUsedSolanaClusterName);

  // Auto-save to cookies whenever state changes
  useEffect(() => {
    localStorageHelpers.set(VIRTUAL_MACHINE_KEY, activeVirtualMachine);
  }, [activeVirtualMachine]);

  useEffect(() => {
    localStorageHelpers.set(ETHEREUM_CHAINID_KEY, String(ethereumVirtualMachineChainId));
  }, [ethereumVirtualMachineChainId]);

  useEffect(() => {
    localStorageHelpers.set(SOLANA_CLUSTERNAME_KEY, solanaVirtualMachineClusterName);
  }, [solanaVirtualMachineClusterName]);

  // Derived state
  const isEthereumVirtualMachineActive = activeVirtualMachine === "ethereum";
  const isSolanaVirtualMachineActive = activeVirtualMachine === "solana";

  return {
    // global context
    activeVirtualMachine,
    // ethereum
    ethereumVirtualMachineChainId,

    isEthereumVirtualMachineActive,
    isSolanaVirtualMachineActive,
    setActiveVirtualMachine,

    setEthereumVirtualMachineChainId,
    setSolanaVirtualMachineClusterName,
    // solana
    solanaVirtualMachineClusterName,
  };
}
